import React from 'react';
import Icon from '../../../components/AppIcon';

const ApplicationProgress = ({ stages, currentStage, onStageClick }) => {
  const getStageStatus = (stageIndex, currentStageIndex) => {
    if (stageIndex < currentStageIndex) return 'completed';
    if (stageIndex === currentStageIndex) return 'current';
    return 'upcoming';
  };

  const getStageIcon = (status) => {
    switch (status) {
      case 'completed':
        return 'CheckCircle';
      case 'current':
        return 'Clock';
      default:
        return 'Circle';
    }
  };

  const getStageColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'current':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-gray-400 bg-gray-100';
    }
  };

  const getConnectorColor = (status) => {
    return status === 'completed' ? 'bg-green-600' : 'bg-gray-300';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h3 className="text-lg font-medium text-foreground mb-6">Application Progress</h3>
      <div className="space-y-4">
        {stages?.map((stage, index) => {
          const status = getStageStatus(index, currentStage);
          const isLast = index === stages?.length - 1;
          
          return (
            <div key={stage?.id} className="relative">
              <div className="flex items-start gap-4">
                {/* Stage Icon */}
                <div className="relative flex-shrink-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getStageColor(status)}`}>
                    <Icon name={getStageIcon(status)} size={20} />
                  </div>
                  
                  {/* Connector Line */}
                  {!isLast && (
                    <div className={`absolute top-10 left-1/2 transform -translate-x-1/2 w-0.5 h-8 ${getConnectorColor(status)}`} />
                  )}
                </div>

                {/* Stage Content */}
                <div className="flex-1 min-w-0 pb-8">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <h4 className="font-medium text-foreground">{stage?.name}</h4>
                      <p className="text-sm text-muted-foreground">{stage?.description}</p>
                    </div>
                    
                    <div className="flex flex-col items-start sm:items-end gap-1">
                      {stage?.date && (
                        <span className="text-xs text-muted-foreground">{stage?.date}</span>
                      )}
                      {stage?.duration && (
                        <span className="text-xs text-muted-foreground">{stage?.duration}</span>
                      )}
                    </div>
                  </div>

                  {/* Stage Details */}
                  {status === 'completed' && stage?.completedBy && (
                    <div className="mt-2 text-sm text-muted-foreground">
                      Completed by {stage?.completedBy}
                    </div>
                  )}

                  {status === 'current' && stage?.nextAction && (
                    <div className="mt-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Icon name="AlertCircle" size={14} className="text-blue-600" />
                        <span className="text-blue-600 font-medium">Next: {stage?.nextAction}</span>
                      </div>
                    </div>
                  )}

                  {/* Stage Actions */}
                  {status === 'current' && stage?.actions && stage?.actions?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {stage?.actions?.map((action, actionIndex) => (
                        <button
                          key={actionIndex}
                          onClick={() => onStageClick && onStageClick(stage?.id, action)}
                          className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors duration-150"
                        >
                          {action}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Progress Summary */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Progress</span>
          <span className="font-medium text-foreground">
            {currentStage + 1} of {stages?.length} stages
          </span>
        </div>
        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentStage + 1) / stages?.length) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default ApplicationProgress;